import { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Collapse,
  Form,
  InputNumber,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { CloudUploadOutlined, WarningOutlined } from '@ant-design/icons';
import type { NocSimInjectResult } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { simApi } from '../api/simApi';
import { INJECTABLE_ERROR_CODES, INJECTABLE_STATUSES } from '../constants';
import { InjectResultView } from './SimResult';

interface StatusFormValues {
  connectorId?: number;
  status: string;
  errorCode: string;
}

interface MeterFormValues {
  connectorId?: number;
  voltageV?: number;
  currentA?: number;
  energyWh?: number;
  temperatureC?: number;
}

interface InjectPanelProps {
  stationId: string | null;
  connectorId: number | null;
  /** `true` si la estación está en la allowlist del VCP. */
  allowed: boolean;
  /** Allowlist vigente, informada por el backend (`/noc/sim/config`). */
  allowlist: string[];
}

/** Panel de inyección uplink al VCP (solo estaciones con VCP admin habilitado). */
export function InjectPanel({ stationId, connectorId, allowed, allowlist }: InjectPanelProps) {
  const { message } = App.useApp();
  const [statusForm] = Form.useForm<StatusFormValues>();
  const [meterForm] = Form.useForm<MeterFormValues>();
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [result, setResult] = useState<NocSimInjectResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sincroniza el conector de ambos formularios con el seleccionado arriba.
  useEffect(() => {
    if (connectorId) {
      statusForm.setFieldValue('connectorId', connectorId);
      meterForm.setFieldValue('connectorId', connectorId);
    }
  }, [connectorId, statusForm, meterForm]);

  const inject = async (key: string, action: string, payload: Record<string, unknown>) => {
    if (!stationId || !allowed) {
      return;
    }
    setSubmitting(key);
    setError(null);
    setResult(null);
    try {
      const response = await simApi.inject({ stationId, action, payload });
      setResult(response);
      message.success(`${action} inyectado en ${stationId}.`);
    } catch (caught) {
      const detail = caught instanceof ApiError ? caught.message : 'No se pudo inyectar el mensaje.';
      setError(detail);
      message.error(detail);
    } finally {
      setSubmitting(null);
    }
  };

  if (!allowed) {
    return (
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        message="Inyección no habilitada para esta estación"
        description={
          <span>
            La inyección uplink solo está permitida para estaciones con API admin de simulador
            (allowlist <code>VCP_ALLOWED_STATIONS</code>): <strong>{allowlist.join(', ') || '—'}</strong>.
            Selecciona {allowlist[0] ?? 'una estación habilitada'} para inyectar <code>StatusNotification</code> o <code>MeterValues</code>. Los
            comandos downlink sí aplican a todas las estaciones de la allowlist de simulación.
          </span>
        }
      />
    );
  }

  const connector = connectorId ?? 1;

  const onStatusFinish = (values: StatusFormValues) => {
    void inject('status', 'StatusNotification', {
      connectorId: values.connectorId ?? connector,
      status: values.status,
      errorCode: values.errorCode,
      timestamp: new Date().toISOString(),
    });
  };

  const onMeterFinish = (values: MeterFormValues) => {
    const sampledValue: Array<Record<string, string>> = [];
    if (values.voltageV !== undefined && values.voltageV !== null) {
      sampledValue.push({ value: String(values.voltageV), measurand: 'Voltage', unit: 'V' });
    }
    if (values.currentA !== undefined && values.currentA !== null) {
      sampledValue.push({ value: String(values.currentA), measurand: 'Current.Import', unit: 'A' });
    }
    if (values.energyWh !== undefined && values.energyWh !== null) {
      sampledValue.push({
        value: String(values.energyWh),
        measurand: 'Energy.Active.Import.Register',
        unit: 'Wh',
      });
    }
    if (values.temperatureC !== undefined && values.temperatureC !== null) {
      sampledValue.push({ value: String(values.temperatureC), measurand: 'Temperature', unit: 'Celsius' });
    }
    if (sampledValue.length === 0) {
      message.warning('Ingresa al menos una métrica para enviar MeterValues.');
      return;
    }
    void inject('meter', 'MeterValues', {
      connectorId: values.connectorId ?? connector,
      meterValue: [{ timestamp: new Date().toISOString(), sampledValue }],
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        Fuerza mensajes OCPP salientes en el VCP de <strong>{stationId}</strong> (conector {connector}).
      </Typography.Text>

      <Space wrap>
        <Tooltip title="StatusNotification Faulted con errorCode GroundFailure → alerta CRITICAL en el NOC">
          <Button
            danger
            icon={<WarningOutlined />}
            loading={submitting === 'fault'}
            onClick={() =>
              void inject('fault', 'StatusNotification', {
                connectorId: connector,
                status: 'Faulted',
                errorCode: 'GroundFailure',
                timestamp: new Date().toISOString(),
              })
            }
          >
            Inyectar Faulted
          </Button>
        </Tooltip>
        <Tooltip title="StatusNotification Available → resuelve la alerta">
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={submitting === 'recover'}
            onClick={() =>
              void inject('recover', 'StatusNotification', {
                connectorId: connector,
                status: 'Available',
                errorCode: 'NoError',
                timestamp: new Date().toISOString(),
              })
            }
          >
            Recuperar (Available)
          </Button>
        </Tooltip>
      </Space>

      <Collapse
        ghost
        items={[
          {
            key: 'status',
            label: 'StatusNotification personalizado',
            children: (
              <Form
                form={statusForm}
                layout="vertical"
                onFinish={onStatusFinish}
                initialValues={{ connectorId: connector, status: 'Available', errorCode: 'NoError' }}
                requiredMark={false}
              >
                <Form.Item label="Conector" name="connectorId">
                  <InputNumber min={1} max={16} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="Estado" name="status" rules={[{ required: true }]}>
                  <Select options={INJECTABLE_STATUSES.map((value) => ({ value, label: value }))} />
                </Form.Item>
                <Form.Item label="errorCode" name="errorCode" rules={[{ required: true }]}>
                  <Select options={INJECTABLE_ERROR_CODES.map((value) => ({ value, label: value }))} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting === 'status'}>
                  Inyectar estado
                </Button>
              </Form>
            ),
          },
          {
            key: 'meter',
            label: 'MeterValues (V / A / Wh / °C)',
            children: (
              <Form
                form={meterForm}
                layout="vertical"
                onFinish={onMeterFinish}
                initialValues={{ connectorId: connector, voltageV: 230, currentA: 16, energyWh: 1000, temperatureC: 35 }}
                requiredMark={false}
              >
                <Form.Item label="Conector" name="connectorId">
                  <InputNumber min={1} max={16} style={{ width: '100%' }} />
                </Form.Item>
                <Space wrap>
                  <Form.Item label="Voltaje (V)" name="voltageV">
                    <InputNumber min={0} max={500} style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item label="Corriente (A)" name="currentA">
                    <InputNumber min={0} max={400} style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item label="Energía (Wh)" name="energyWh">
                    <InputNumber min={0} style={{ width: 140 }} />
                  </Form.Item>
                  <Form.Item label="Temperatura (°C)" name="temperatureC">
                    <InputNumber min={-40} max={120} style={{ width: 140 }} />
                  </Form.Item>
                </Space>
                <Button type="primary" htmlType="submit" loading={submitting === 'meter'}>
                  Enviar MeterValues
                </Button>
                <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  Las métricas se reflejan en el live-state (Redis) y emiten <code>meter.tick</code>. Una
                  temperatura ≥ 65 °C abre alerta <code>OVER_TEMPERATURE</code>.
                </Typography.Paragraph>
              </Form>
            ),
          },
        ]}
      />

      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      {result && <InjectResultView result={result} />}
    </Space>
  );
}
